import { supabase } from './supabase';

export interface WorkspaceMemberWithProfile {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'manager' | 'member';
  avatar?: string;
  joinedDate: string;
  lastActive: string;
  status: 'active' | 'inactive';
}

/**
 * Fetches all active members of a workspace with their profile information
 */
export async function fetchWorkspaceMembers(workspaceId: string): Promise<WorkspaceMemberWithProfile[]> {
  try {
    // First, fetch workspace members
    const { data: membersData, error: membersError } = await supabase
      .from('workspace_members')
      .select('id, user_id, role, status, joined_at')
      .eq('workspace_id', workspaceId)
      .eq('status', 'active')
      .order('joined_at', { ascending: true });

    if (membersError) {
      console.error('Error fetching workspace members:', membersError);
      return [];
    }

    if (!membersData || membersData.length === 0) {
      return [];
    }

    // Fetch all profiles in a single batch query instead of N+1 queries
    const userIds = membersData.map(member => member.user_id);
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, avatar_url, last_sign_in_at')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching profiles:', profilesError);
      // Return members without profile data if profile fetch fails
      return membersData.map(member => ({
        id: member.user_id,
        name: 'Unknown User',
        email: '',
        role: member.role as 'owner' | 'admin' | 'manager' | 'member',
        avatar: undefined,
        joinedDate: member.joined_at,
        lastActive: member.joined_at,
        status: member.status as 'active' | 'inactive',
      }));
    }

    // Create a map for O(1) profile lookup
    const profilesMap = new Map(
      (profilesData || []).map(profile => [profile.id, profile])
    );

    // Combine members with their profiles
    const membersWithProfiles = membersData.map((member) => {
      const profileData = profilesMap.get(member.user_id);
      const firstName = profileData?.first_name || '';
      const lastName = profileData?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim() || profileData?.email?.split('@')[0] || 'Unknown User';

      return {
        id: member.user_id,
        name: fullName,
        email: profileData?.email || '',
        role: member.role as 'owner' | 'admin' | 'manager' | 'member',
        avatar: profileData?.avatar_url,
        joinedDate: member.joined_at,
        lastActive: profileData?.last_sign_in_at || member.joined_at,
        status: member.status as 'active' | 'inactive',
      };
    });

    return membersWithProfiles;
  } catch (error) {
    console.error('Error fetching workspace members:', error);
    return [];
  }
}
